import { Link, useSearchParams, useParams } from "react-router-dom";
import { useCalculationGraph } from "../hooks/useCalculationGraph";
import { CalculationGraph } from "../components/CalculationGraph/CalculationGraph";
import type { CalculationGraph as CalculationGraphData } from "../types/shadow-run";
import styles from "./GraphExplorerScreen.module.css";

function GraphExplorerContent({
  batchId,
  packetId,
  orgCategory,
  orgId,
}: {
  batchId: string;
  packetId: string;
  orgCategory: string;
  orgId: string;
}) {
  const { data, isLoading, isError } = useCalculationGraph(
    orgCategory,
    orgId,
    batchId,
    packetId,
  );

  if (isLoading) {
    return <p className={styles.message}>Loading calculation graph…</p>;
  }

  if (isError || !data) {
    return (
      <p className={styles.message}>
        Unable to load calculation graph. Please try again.
      </p>
    );
  }

  const graph: CalculationGraphData = data;

  return <CalculationGraph graph={graph} />;
}

export function GraphExplorerScreen() {
  const { batchId, packetId } = useParams<{
    batchId: string;
    packetId: string;
  }>();
  const [searchParams] = useSearchParams();
  const orgCategory = searchParams.get("org_category");
  const orgId = searchParams.get("org_id");

  if (!batchId || !packetId) {
    return (
      <div className={styles.page}>
        <p className={styles.message}>
          Missing batch or packet ID. Please navigate from the packet detail
          screen.
        </p>
      </div>
    );
  }

  if (!orgCategory || !orgId) {
    return (
      <div className={styles.page}>
        <p className={styles.message}>
          Missing organisation parameters. Please navigate from the packet
          detail screen.
        </p>
      </div>
    );
  }

  const qp = `org_category=${encodeURIComponent(orgCategory)}&org_id=${encodeURIComponent(orgId)}`;

  return (
    <div className={styles.page}>
      <nav className={styles.breadcrumb} aria-label="Breadcrumb">
        <Link to="/shadow-runs" className={styles.breadcrumbLink}>
          Shadow Runs
        </Link>
        <span className={styles.breadcrumbSep}>/</span>
        <Link
          to={`/shadow-runs/${batchId}?${qp}`}
          className={styles.breadcrumbLink}
        >
          {batchId}
        </Link>
        <span className={styles.breadcrumbSep}>/</span>
        <Link
          to={`/shadow-runs/${batchId}/packets?${qp}`}
          className={styles.breadcrumbLink}
        >
          Packets
        </Link>
        <span className={styles.breadcrumbSep}>/</span>
        <Link
          to={`/shadow-runs/${batchId}/packets/${packetId}?${qp}`}
          className={styles.breadcrumbLink}
        >
          {packetId}
        </Link>
        <span className={styles.breadcrumbSep}>/</span>
        <span className={styles.breadcrumbCurrent}>Graph</span>
      </nav>

      <GraphExplorerContent
        batchId={batchId}
        packetId={packetId}
        orgCategory={orgCategory}
        orgId={orgId}
      />
    </div>
  );
}
